// @ts-nocheck
import {
  SPELL_IMPACT_POOLS,
  allSpellSfxPaths,
  spellSfx,
} from "./data";
import {
  clamp,
  track,
} from "./util";
import {
  assetURL,
} from "./sprites";

let firesprayImpactSeq = 0;

/* ---------------- Sound (WebAudio + optional audio files) ----------------
   Bus layout: UI | SFX | Music → void-muffle (lowpass) → master → speakers.
   Void indicator bypasses the muffle bus (dry to master). Master mute/volume
   applies to all channels; per-channel levels (wlkVolUi / wlkVolSfx / wlkVolMusic). */
export const Sound = (() => {
  const CH = { music: "music", sfx: "sfx", ui: "ui" };
  const CH_KEYS = [CH.music, CH.sfx, CH.ui];
  const BASE_MIX = 0.1; // global level (20% of prior 0.5 baseline; sliders stack on top)
  // Synthesized SFX peaks are tiny vs full-scale music MP3s; boost the bus to balance.
  const CH_BOOST = { music: 0.5, sfx: 3, ui: 3.75 };

  let ctx = null, master = null, chBus = {}, muffleFilter = null, muffleShelf = null, muffleGain = null;
  let muffleActive = false, muffleK = -1;
  const MUFFLE_OPEN_FREQ = 8000; // BiquadFilter nominal max; 22kHz clamps + spams the console
  let noiseBuf = null;
  let muted = false, volume = 0.8;
  let sfxSuppress = 0;
  const chVol = { music: 1, sfx: 1, ui: 1 };
  const chMuted = { music: false, sfx: false, ui: false };
  const fileBuf = {};   // path -> AudioBuffer
  const fileLoad = {};  // path -> Promise

  function chVolKey(id) {
    return "wlkVol" + id.charAt(0).toUpperCase() + id.slice(1);
  }
  function chMuteKey(id) {
    return "wlkMute" + id.charAt(0).toUpperCase() + id.slice(1);
  }

  try {
    muted = localStorage.getItem("wlkMuted") === "1";
    const v = parseFloat(localStorage.getItem("wlkVol")); if (!isNaN(v)) volume = v;
    for (const id of CH_KEYS) {
      const cv = parseFloat(localStorage.getItem(chVolKey(id)));
      if (!isNaN(cv)) chVol[id] = Math.max(0, Math.min(1, cv));
      chMuted[id] = localStorage.getItem(chMuteKey(id)) === "1";
    }
  } catch (e) {}

  function chAudible(ch) {
    if (ch === CH.sfx && sfxSuppress > 0) return false;
    return !muted && !chMuted[ch] && chVol[ch] > 0;
  }

  function syncGains() {
    if (master) master.gain.value = muted ? 0 : volume * BASE_MIX;
    for (const id of CH_KEYS) {
      if (chBus[id]) chBus[id].gain.value = chMuted[id] ? 0 : chVol[id] * (CH_BOOST[id] || 1);
    }
  }

  const ensure = () => {
    if (!ctx) {
      try {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        master = ctx.createGain();
        master.connect(ctx.destination);
        muffleFilter = ctx.createBiquadFilter();
        muffleFilter.type = "lowpass";
        muffleFilter.frequency.value = MUFFLE_OPEN_FREQ;
        muffleFilter.Q.value = 0.7;
        muffleShelf = ctx.createBiquadFilter();
        muffleShelf.type = "highshelf";
        muffleShelf.frequency.value = 900;
        muffleShelf.gain.value = 0;
        muffleGain = ctx.createGain();
        muffleGain.gain.value = 1;
        muffleFilter.connect(muffleShelf);
        muffleShelf.connect(muffleGain);
        muffleGain.connect(master);
        for (const id of CH_KEYS) {
          const g = ctx.createGain();
          g.connect(muffleFilter);
          chBus[id] = g;
        }
        syncGains();
      } catch (e) {}
    }
    return ctx;
  };

  function bus(ch) {
    return chBus[ch] || master;
  }

  function tone(freq, dur, o = {}) {
    const ch = o.channel || CH.sfx;
    const c = ensure(); if (!c || !chAudible(ch)) return;
    const t0 = c.currentTime + (o.delay || 0);
    const osc = c.createOscillator(), g = c.createGain();
    osc.type = o.type || "sine";
    osc.frequency.setValueAtTime(freq, t0);
    if (o.slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.slideTo), t0 + dur);
    if (ch !== CH.ui) applyOscPitch(osc, !!o.self, t0);
    const peak = o.gain != null ? o.gain : 0.06;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + (o.attack || 0.006));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(bus(ch));
    osc.start(t0); osc.stop(t0 + dur + 0.03);
  }

  function noise(dur, o = {}) {
    const ch = o.channel || CH.sfx;
    const c = ensure(); if (!c || !chAudible(ch)) return;
    if (!noiseBuf) {
      noiseBuf = c.createBuffer(1, c.sampleRate, c.sampleRate);
      const d = noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    const t0 = c.currentTime + (o.delay || 0);
    const src = c.createBufferSource(); src.buffer = noiseBuf;
    if (ch !== CH.ui) applyBufferPitch(src, !!o.self, t0);
    const f = c.createBiquadFilter();
    f.type = o.type || "lowpass"; f.frequency.setValueAtTime(o.freq || 1000, t0); f.Q.value = o.q || 0.7;
    if (o.slideTo) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.slideTo), t0 + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(o.gain != null ? o.gain : 0.06, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f); f.connect(g); g.connect(bus(ch));
    src.start(t0); src.stop(t0 + dur + 0.03);
  }

  const last = {};
  function throttle(key, gap) {
    const now = ctx ? ctx.currentTime : 0;
    if (last[key] && now - last[key] < gap) return false;
    last[key] = now; return true;
  }

  const assetV = () => window.WLK_V || "dev";

  function loadFile(path) {
    if (fileBuf[path]) return Promise.resolve(fileBuf[path]);
    if (fileLoad[path]) return fileLoad[path];
    const c = ensure();
    if (!c) return Promise.resolve(null);
    fileLoad[path] = fetch(assetURL(path) + "?v=" + assetV())
      .then(r => { if (!r.ok) throw new Error("audio " + r.status); return r.arrayBuffer(); })
      .then(ab => new Promise((res, rej) => c.decodeAudioData(ab, res, rej)))
      .then(buf => { fileBuf[path] = buf; delete fileLoad[path]; return buf; })
      .catch(() => { delete fileLoad[path]; return null; });
    return fileLoad[path];
  }

  function playFile(path, o = {}) {
    const ch = o.channel || FILE_CHANNEL[path] || CH.sfx;
    if (!chAudible(ch)) return;
    loadFile(path).then(buf => {
      const c = ensure(); if (!c || !buf || !chAudible(ch)) return;
      const src = c.createBufferSource();
      src.buffer = buf;
      if (ch !== CH.ui) applyBufferPitch(src, !!o.self, c.currentTime);
      trackPitchSrc(src, !!o.self);
      const g = c.createGain();
      const fileMul = FILE_GAIN[path] != null ? FILE_GAIN[path] : 1;
      const callMul = o.gain != null ? o.gain : 1;
      g.gain.value = fileMul * callMul;
      src.connect(g); g.connect(bus(ch));
      src.start(0);
    });
  }

  let musicLoopSrc = null;
  let musicLoopPath = "";
  let wantedLoop = ""; // track intended loop; restart after AudioContext unlock
  let loopNeedsRestart = false;
  let overlayLoopSrc = null;
  let overlayLoopPath = "";
  let wantedOverlay = "";
  let overlayNeedsRestart = false;
  let voidIndSrc = null;
  let voidIndGain = null;
  let voidIndIntensity = -1;

  const BULLET_TIME_START = "audio/skills/bulletTime/bullet_time_start.mp3";
  const BULLET_TIME_END = "audio/skills/bulletTime/bullet_time_end.mp3";
  const NUKE_DETONATION = "audio/skills/nuke/nuke_detonation.mp3";
  const BULLET_TIME_START_DUR = 2.847;
  const BULLET_TIME_END_DUR = 1.102;
  const NUKE_DETONATION_DUR = 8.057;
  const BULLET_TIME_RATE = 0.5;
  let bulletTimeAmOwner = false;
  let worldPitch = 1;
  let pitchRamp = null; // { from, to, startT, endT } in AudioContext time
  const activePitchSrc = new Map(); // BufferSource -> isSelf

  function btSfxDuration(path, fallback) {
    const buf = fileBuf[path];
    return buf && buf.duration > 0 ? buf.duration : fallback;
  }

  function shouldWorldPitch(isSelf) {
    return !(bulletTimeAmOwner && isSelf);
  }

  function currentWorldPitch(t) {
    if (!pitchRamp) return worldPitch;
    if (t >= pitchRamp.endT) {
      worldPitch = pitchRamp.to;
      pitchRamp = null;
      return worldPitch;
    }
    if (t <= pitchRamp.startT) return pitchRamp.from;
    const k = (t - pitchRamp.startT) / (pitchRamp.endT - pitchRamp.startT);
    return pitchRamp.from + (pitchRamp.to - pitchRamp.from) * k;
  }

  function pitchRateFor(isSelf, t) {
    if (!shouldWorldPitch(isSelf)) return 1;
    return currentWorldPitch(t);
  }

  function rateToDetune(rate) {
    return rate === 1 ? 0 : 1200 * Math.log2(rate);
  }

  function trackPitchSrc(src, isSelf = false) {
    if (!src) return;
    activePitchSrc.set(src, !!isSelf);
    const prev = src.onended;
    src.onended = (ev) => {
      activePitchSrc.delete(src);
      if (typeof prev === "function") prev.call(src, ev);
    };
  }

  function setBufferPitch(src, rate, t0) {
    if (!src) return;
    src.playbackRate.cancelScheduledValues(t0);
    src.playbackRate.setValueAtTime(rate, t0);
  }

  function rampBufferPitch(src, from, to, t0, duration) {
    if (!src) return;
    if (duration <= 0 || Math.abs(from - to) < 0.001) {
      setBufferPitch(src, to, t0);
      return;
    }
    src.playbackRate.cancelScheduledValues(t0);
    src.playbackRate.setValueAtTime(from, t0);
    src.playbackRate.linearRampToValueAtTime(to, t0 + duration);
  }

  function applyBufferPitch(src, isSelf, t0) {
    if (!shouldWorldPitch(isSelf)) {
      setBufferPitch(src, 1, t0);
      return;
    }
    if (pitchRamp && t0 < pitchRamp.endT) {
      rampBufferPitch(src, currentWorldPitch(t0), pitchRamp.to, t0, pitchRamp.endT - t0);
      return;
    }
    setBufferPitch(src, currentWorldPitch(t0), t0);
  }

  function setOscPitch(osc, rate, t0) {
    if (!osc) return;
    osc.detune.cancelScheduledValues(t0);
    osc.detune.setValueAtTime(rateToDetune(rate), t0);
  }

  function applyOscPitch(osc, isSelf, t0) {
    if (!osc) return;
    if (!shouldWorldPitch(isSelf)) {
      setOscPitch(osc, 1, t0);
      return;
    }
    if (pitchRamp && t0 < pitchRamp.endT) {
      const from = rateToDetune(currentWorldPitch(t0));
      const to = rateToDetune(pitchRamp.to);
      const dur = pitchRamp.endT - t0;
      osc.detune.cancelScheduledValues(t0);
      osc.detune.setValueAtTime(from, t0);
      osc.detune.linearRampToValueAtTime(to, t0 + dur);
      return;
    }
    setOscPitch(osc, currentWorldPitch(t0), t0);
  }

  function rampAllSources(from, to, t0, duration) {
    if (musicLoopSrc) rampBufferPitch(musicLoopSrc, from, to, t0, duration);
    if (overlayLoopSrc) rampBufferPitch(overlayLoopSrc, from, to, t0, duration);
    for (const entry of fwLoops.values()) {
      if (entry && entry.src) rampBufferPitch(entry.src, from, to, t0, duration);
    }
    for (const [src, isSelf] of activePitchSrc) {
      if (shouldWorldPitch(isSelf)) rampBufferPitch(src, from, to, t0, duration);
    }
  }

  function beginWorldPitchRamp(to, duration) {
    const c = ensure();
    if (!c || duration <= 0) return;
    const now = c.currentTime;
    const from = currentWorldPitch(now);
    if (Math.abs(from - to) < 0.001) {
      worldPitch = to;
      pitchRamp = null;
      return;
    }
    pitchRamp = { from, to, startT: now, endT: now + duration };
    rampAllSources(from, to, now, duration);
  }

  function updateBulletTime(_active, amOwner) {
    bulletTimeAmOwner = !!amOwner;
  }

  function setVoidMuffle(active, intensity = 0) {
    ensure();
    if (!muffleFilter || !muffleShelf || !muffleGain || !ctx) return;
    const k = active ? clamp(intensity, 0, 1) : -1;
    if (active === muffleActive && (active ? Math.abs(k - muffleK) < 0.02 : true)) return;
    muffleActive = active;
    muffleK = k;
    const now = ctx.currentTime;
    const ramp = 0.22;
    if (active) {
      const freq = Math.max(90, 420 - k * 320);
      const vol = Math.max(0.55, 0.7 - k * 0.1);
      muffleFilter.frequency.setTargetAtTime(freq, now, ramp);
      muffleFilter.Q.setTargetAtTime(2.2 + k * 1.3, now, ramp);
      muffleShelf.gain.setTargetAtTime(-10 - k * 4, now, ramp);
      muffleGain.gain.setTargetAtTime(vol, now, ramp);
    } else {
      muffleFilter.frequency.setTargetAtTime(MUFFLE_OPEN_FREQ, now, ramp);
      muffleFilter.Q.setTargetAtTime(0.7, now, ramp);
      muffleShelf.gain.setTargetAtTime(0, now, ramp);
      muffleGain.gain.setTargetAtTime(1, now, ramp);
    }
  }

  const fwLoops = new Map();
  let previewAudioDepth = 0;
  const previewFwLoopKeys = new Set();
  const FW_LOOP_FADE = 0.14;
  const FW_LOOP_ATTACK = 0.02; // gain ramp — loop file is trimmed at source

  function pushPreviewAudio() {
    previewAudioDepth++;
  }

  function popPreviewAudio() {
    if (previewAudioDepth <= 0) return;
    previewAudioDepth--;
    if (previewAudioDepth === 0) {
      for (const key of [...previewFwLoopKeys]) haltFirewallLoop(key, true);
      previewFwLoopKeys.clear();
    }
  }

  function haltFirewallLoop(key, fade) {
    const entry = fwLoops.get(key);
    if (!entry) return;
    fwLoops.delete(key);
    previewFwLoopKeys.delete(key);
    const { src, gain } = entry;
    const c = ensure();
    if (!c || !gain) {
      try { src.stop(); } catch (e) {}
      try { src.disconnect(); } catch (e) {}
      return;
    }
    const now = c.currentTime;
    if (fade) {
      gain.gain.setTargetAtTime(0.0001, now, FW_LOOP_FADE * 0.28);
      window.setTimeout(() => {
        try { src.stop(); } catch (e) {}
        try { src.disconnect(); } catch (e) {}
        try { gain.disconnect(); } catch (e) {}
      }, FW_LOOP_FADE * 1000 + 40);
    } else {
      try { src.stop(); } catch (e) {}
      try { src.disconnect(); } catch (e) {}
      try { gain.disconnect(); } catch (e) {}
    }
  }

  function startFirewallLoop(key, path) {
    if (fwLoops.has(key) || !path || !chAudible(CH.sfx)) return;
    loadFile(path).then(buf => {
      const c = ensure();
      if (!c || !buf || !chAudible(CH.sfx) || fwLoops.has(key)) return;
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      applyBufferPitch(src, false, c.currentTime);
      const g = c.createGain();
      const now = c.currentTime;
      const vol = (FILE_GAIN[path] != null ? FILE_GAIN[path] : 1) * 0.82;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), now + FW_LOOP_ATTACK);
      src.connect(g);
      g.connect(bus(CH.sfx));
      src.start(0);
      fwLoops.set(key, { src, gain: g });
      if (previewAudioDepth > 0) previewFwLoopKeys.add(key);
    });
  }

  function haltAllFirewallLoops(fade) {
    for (const key of [...fwLoops.keys()]) haltFirewallLoop(key, fade);
  }

  function haltVoidIndicator() {
    if (!voidIndSrc) return;
    try { voidIndSrc.stop(); } catch (e) {}
    try { voidIndSrc.disconnect(); } catch (e) {}
    if (voidIndGain) try { voidIndGain.disconnect(); } catch (e) {}
    voidIndSrc = null;
    voidIndGain = null;
    voidIndIntensity = -1;
  }

  function updateVoidIndicator(intensity) {
    const t = clamp(intensity, 0, 1);
    if (!chAudible(CH.sfx)) { haltVoidIndicator(); setVoidMuffle(false); return; }
    setVoidMuffle(true, t);
    loadFile(VOID_INDICATOR).then(buf => {
      const c = ensure();
      if (!c || !buf || !chAudible(CH.sfx)) return;
      const gain = (FILE_GAIN[VOID_INDICATOR] != null ? FILE_GAIN[VOID_INDICATOR] : 1) * (0.45 + 0.95 * t);
      const offset = t * Math.max(0, buf.duration - 0.08);
      const restart = !voidIndSrc || t > voidIndIntensity + 0.05;
      if (voidIndSrc && !restart) {
        if (voidIndGain) voidIndGain.gain.value = gain;
        setVoidMuffle(true, t);
        return;
      }
      haltVoidIndicator();
      voidIndIntensity = t;
      const src = c.createBufferSource();
      src.buffer = buf;
      const g = c.createGain();
      g.gain.value = gain;
      src.connect(g); g.connect(master);
      const playLen = Math.max(0.05, buf.duration - offset);
      src.start(0, offset, playLen);
      voidIndSrc = src;
      voidIndGain = g;
      src.onended = () => {
        if (voidIndSrc === src) { voidIndSrc = null; voidIndGain = null; voidIndIntensity = -1; }
      };
    });
  }

  function haltMusicLoop() {
    if (!musicLoopSrc) return;
    try { musicLoopSrc.stop(); } catch (e) {}
    try { musicLoopSrc.disconnect(); } catch (e) {}
    musicLoopSrc = null;
    musicLoopPath = "";
  }

  function stopMusicLoop() {
    wantedLoop = "";
    loopNeedsRestart = false;
    haltMusicLoop();
  }

  function startMusicPlay(path, loop = true) {
    wantedLoop = loop ? path : "";
    const ch = FILE_CHANNEL[path] || CH.music;
    if (musicLoopPath === path && musicLoopSrc) return;
    haltMusicLoop();
    if (!chAudible(ch)) return;
    loadFile(path).then(buf => {
      const c = ensure();
      if (!c || !buf || !chAudible(ch)) return;
      if (loop && wantedLoop !== path) return;
      if (musicLoopPath === path && musicLoopSrc) return;
      haltMusicLoop();
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = loop;
      applyBufferPitch(src, false, c.currentTime);
      const g = c.createGain();
      g.gain.value = FILE_GAIN[path] != null ? FILE_GAIN[path] : 1;
      src.connect(g); g.connect(bus(ch));
      src.start(0);
      musicLoopSrc = src;
      musicLoopPath = path;
      loopNeedsRestart = loop && c.state === "suspended";
      src.onended = () => {
        if (musicLoopSrc === src) { musicLoopSrc = null; musicLoopPath = ""; }
      };
    });
  }

  function startMusicLoop(path) { startMusicPlay(path, true); }

  function haltOverlayLoop() {
    if (!overlayLoopSrc) return;
    try { overlayLoopSrc.stop(); } catch (e) {}
    try { overlayLoopSrc.disconnect(); } catch (e) {}
    overlayLoopSrc = null;
    overlayLoopPath = "";
  }

  function stopOverlayLoop() {
    wantedOverlay = "";
    overlayNeedsRestart = false;
    haltOverlayLoop();
  }

  function startOverlayLoop(path) {
    wantedOverlay = path;
    const ch = FILE_CHANNEL[path] || CH.music;
    if (overlayLoopPath === path && overlayLoopSrc) return;
    haltOverlayLoop();
    if (!chAudible(ch)) return;
    loadFile(path).then(buf => {
      const c = ensure();
      if (!c || !buf || !chAudible(ch)) return;
      if (wantedOverlay !== path) return;
      if (overlayLoopPath === path && overlayLoopSrc) return;
      haltOverlayLoop();
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      applyBufferPitch(src, false, c.currentTime);
      const g = c.createGain();
      g.gain.value = FILE_GAIN[path] != null ? FILE_GAIN[path] : 1;
      src.connect(g);
      g.connect(bus(ch));
      src.start(0);
      overlayLoopSrc = src;
      overlayLoopPath = path;
      overlayNeedsRestart = c.state === "suspended";
      src.onended = () => {
        if (overlayLoopSrc === src) { overlayLoopSrc = null; overlayLoopPath = ""; }
      };
    });
  }

  function restartWantedOverlay() {
    if (!wantedOverlay) return;
    if (!overlayNeedsRestart && overlayLoopPath === wantedOverlay && overlayLoopSrc) return;
    overlayNeedsRestart = false;
    haltOverlayLoop();
    startOverlayLoop(wantedOverlay);
  }

  let creditPreviewSrc = null;
  let creditPreviewPath = "";
  let creditPreviewEndCb = null;

  function stopCreditPreview() {
    const had = creditPreviewPath;
    if (creditPreviewSrc) {
      try { creditPreviewSrc.stop(); } catch (e) {}
      try { creditPreviewSrc.disconnect(); } catch (e) {}
    }
    creditPreviewSrc = null;
    creditPreviewPath = "";
    if (had && creditPreviewEndCb) creditPreviewEndCb();
  }

  function fileGain(path) {
    return FILE_GAIN[path] != null ? FILE_GAIN[path] : 1;
  }

  function toggleCreditPreview(path, loop = true) {
    ensure();
    if (creditPreviewPath === path) {
      stopCreditPreview();
      return "";
    }
    stopCreditPreview();
    stopMusicLoop();
    const ch = FILE_CHANNEL[path] || CH.music;
    if (!chAudible(ch)) return "";
    creditPreviewPath = path;
    loadFile(path).then(buf => {
      const c = ensure();
      if (!c || !buf || !chAudible(ch)) {
        if (creditPreviewPath === path) creditPreviewPath = "";
        return;
      }
      if (creditPreviewPath !== path) return;
      const src = c.createBufferSource();
      src.buffer = buf;
      src.loop = !!loop;
      const g = c.createGain();
      g.gain.value = fileGain(path);
      src.connect(g);
      g.connect(bus(ch));
      src.onended = () => {
        if (creditPreviewSrc === src) {
          creditPreviewSrc = null;
          creditPreviewPath = "";
          if (creditPreviewEndCb) creditPreviewEndCb();
        }
      };
      src.start(0);
      creditPreviewSrc = src;
      creditPreviewPath = path;
    });
    return path;
  }

  function restartWantedLoop() {
    if (!wantedLoop) return;
    if (!loopNeedsRestart && musicLoopPath === wantedLoop && musicLoopSrc) return;
    loopNeedsRestart = false;
    haltMusicLoop();
    startMusicLoop(wantedLoop);
  }

  function restartAllWantedLoops() {
    restartWantedLoop();
    restartWantedOverlay();
  }

  // const COMBAT_START_HORN = "audio/combat/start_combat_horn_light.mp3";
  const COMBAT_START_HORN = "audio/combat/start_combat_horn_dark.mp3";
  const VOID_INDICATOR = "audio/sfx/void_indicator.mp3";
  const ROUND_AWARD = "audio/sfx/round_award.mp3";
  const MENU_UI_MUSIC = "audio/menu/illusory_realm_ui_music.mp3";
  const MATCHMAKING_QUEUE_MUSIC = "audio/sfx/ui/war_drums_loop.mp3";
  const READY_CHECK_GONG = "audio/sfx/ui/ready_check_gong.mp3";
  const HELLO_THERE_EASTER_EGG = "audio/sfx/ui/hello_there_easter_egg.mp3";
  const INTERMISSION_TRACKS = [
    "audio/menu/myst_on_the_moor_ui_music.mp3",
    "audio/menu/infinitum_ui_music.mp3",
    "audio/menu/tempting_secrets_ui_music.mp3",
  ];
  function intermissionTrack(round) {
    const n = Math.max(1, round | 0);
    return INTERMISSION_TRACKS[(n - 1) % INTERMISSION_TRACKS.length];
  }
  const COMBAT_MUSIC_BY_ID = {
    goliath: "audio/combat/goliath_combat_music.mp3",
    boss1: "audio/boss/boss_battle_1.mp3",
    boss2: "audio/boss/boss_battle_2.mp3",
    boss3: "audio/boss/boss_battle_3.mp3",
  };
  const COMBAT_MUSIC_TRACKS = Object.values(COMBAT_MUSIC_BY_ID);
  function combatMusicPath(id) {
    return COMBAT_MUSIC_BY_ID[id] || COMBAT_MUSIC_TRACKS[0];
  }
  // Per-asset mix (0–1) on its channel bus; channel default in FILE_CHANNEL.
  const FILE_GAIN = {
    [COMBAT_START_HORN]: 0.425,
    [VOID_INDICATOR]: 0.7,
    [ROUND_AWARD]: 0.35,
    [READY_CHECK_GONG]: 1.0,
    [HELLO_THERE_EASTER_EGG]: 5.0,
    [BULLET_TIME_START]: 0.85,
    [BULLET_TIME_END]: 0.85,
    [NUKE_DETONATION]: 0.7,
  };
  const FILE_CHANNEL = {
    [COMBAT_START_HORN]: CH.music,
    [VOID_INDICATOR]: CH.sfx,
    [ROUND_AWARD]: CH.sfx,
    [READY_CHECK_GONG]: CH.sfx,
    [HELLO_THERE_EASTER_EGG]: CH.sfx,
    [BULLET_TIME_START]: CH.sfx,
    [BULLET_TIME_END]: CH.sfx,
    [NUKE_DETONATION]: CH.sfx,
    [MENU_UI_MUSIC]: CH.music,
    [MATCHMAKING_QUEUE_MUSIC]: CH.music,
  };
  // Per-track music gain — tune in MUSIC_TRACK_GAIN / web/audio/music-gain.md (omit = 1.0).
  const MUSIC_TRACK_GAIN = {
    [MENU_UI_MUSIC]: 1.0,
    [MATCHMAKING_QUEUE_MUSIC]: 1.0,
    [INTERMISSION_TRACKS[0]]: 1.0,   // myst_on_the_moor (quiet master)
    [INTERMISSION_TRACKS[1]]: 1.0,  // infinitum (hot)
    [INTERMISSION_TRACKS[2]]: 1.0,  // tempting_secrets
    [COMBAT_MUSIC_BY_ID.goliath]: 0.70,
    [COMBAT_MUSIC_BY_ID.boss1]: 0.70, // Dragon Castle
    [COMBAT_MUSIC_BY_ID.boss2]: 0.70, // Endless Storm
    [COMBAT_MUSIC_BY_ID.boss3]: 0.70, // The Army of Minotaur
  };
  for (const [path, gain] of Object.entries(MUSIC_TRACK_GAIN)) {
    if (gain != null) FILE_GAIN[path] = gain;
    FILE_CHANNEL[path] = CH.music;
  }
  for (const p of COMBAT_MUSIC_TRACKS) FILE_CHANNEL[p] = CH.music;
  for (const p of INTERMISSION_TRACKS) FILE_CHANNEL[p] = CH.music;
  for (const p of allSpellSfxPaths()) {
    FILE_GAIN[p] = 0.55;
    FILE_CHANNEL[p] = CH.sfx;
  }

  const api = {
    CH,
    tone,
    noise,
    suspend: () => {
      const c = ctx;
      if (c && c.state === "running") return c.suspend();
      return Promise.resolve();
    },
    resume: () => {
      const c = ensure();
      const after = () => restartAllWantedLoops();
      if (c && c.state === "suspended") c.resume().then(after);
      else after();
      loadFile(COMBAT_START_HORN);
      loadFile(VOID_INDICATOR);
      loadFile(ROUND_AWARD);
      for (const p of allSpellSfxPaths()) loadFile(p);
      for (const p of COMBAT_MUSIC_TRACKS) loadFile(p);
      loadFile(MENU_UI_MUSIC);
      loadFile(MATCHMAKING_QUEUE_MUSIC);
      loadFile(READY_CHECK_GONG);
      loadFile(HELLO_THERE_EASTER_EGG);
      loadFile(BULLET_TIME_START);
      loadFile(BULLET_TIME_END);
      loadFile(NUKE_DETONATION);
      for (const p of INTERMISSION_TRACKS) loadFile(p);
    },
    combatMusicPath,
    startCombatMusic: (path) => { if (path) startMusicLoop(path); },
    startMenuMusic: () => startMusicLoop(MENU_UI_MUSIC),
    startLobbyMusic: () => startMusicLoop(MENU_UI_MUSIC),
    beginReadyCheckAudio: () => {
      const c = ensure();
      const play = () => {
        if (!throttle("readyCheckGong", 1.2) || !chAudible(CH.sfx)) return;
        playFile(READY_CHECK_GONG);
      };
      if (c && c.state === "suspended") c.resume().then(play);
      else play();
    },
    startIntermissionMusic: (round) => startMusicPlay(intermissionTrack(round), false),
    stopCombatMusic: () => stopMusicLoop(),
    fileGain,
    creditPreviewPath: () => creditPreviewPath,
    stopCreditPreview,
    toggleCreditPreview,
    setCreditPreviewEndCb: (fn) => { creditPreviewEndCb = typeof fn === "function" ? fn : null; },
    isMuted: () => muted,
    setMuted: (m) => {
      muted = !!m;
      try { localStorage.setItem("wlkMuted", muted ? "1" : "0"); } catch (e) {}
      syncGains();
    },
    toggleMute: () => { api.setMuted(!muted); return muted; },
    masterVolume: () => volume,
    setMasterVolume: (v) => {
      volume = Math.max(0, Math.min(1, v));
      try { localStorage.setItem("wlkVol", String(volume)); } catch (e) {}
      syncGains();
    },
    channelVolume: (ch) => chVol[ch] != null ? chVol[ch] : 1,
    setChannelVolume: (ch, v) => {
      if (chVol[ch] == null) return;
      chVol[ch] = Math.max(0, Math.min(1, v));
      try { localStorage.setItem(chVolKey(ch), String(chVol[ch])); } catch (e) {}
      syncGains();
    },
    channelVolumePct: (ch) => Math.round(api.channelVolume(ch) * 100),
    setChannelVolumePct: (ch, pct) => api.setChannelVolume(ch, Math.max(0, Math.min(100, pct)) / 100),
    masterVolumePct: () => Math.round(volume * 100),
    setMasterVolumePct: (pct) => api.setMasterVolume(Math.max(0, Math.min(100, pct)) / 100),
    isChannelMuted: (ch) => !!chMuted[ch],
    setChannelMuted: (ch, m) => {
      if (chMuted[ch] == null) return;
      chMuted[ch] = !!m;
      try { localStorage.setItem(chMuteKey(ch), chMuted[ch] ? "1" : "0"); } catch (e) {}
      syncGains();
    },
    toggleChannelMute: (ch) => { api.setChannelMuted(ch, !chMuted[ch]); return chMuted[ch]; },
    pushSfxSuppress: () => { sfxSuppress++; },
    popSfxSuppress: () => { sfxSuppress = Math.max(0, sfxSuppress - 1); },

    stickyMinePlant: () => {
      if (!throttle("smp", 0.05) || !chAudible(CH.sfx)) return;
      tone(1180, 0.06, { type: "square", gain: 0.046 });
      tone(880, 0.09, { type: "square", gain: 0.038, delay: 0.055 });
      noise(0.35, { gain: 0.07, freq: 650, slideTo: 110 });
      tone(105, 0.32, { type: "sawtooth", gain: 0.055, delay: 0.04, slideTo: 42 });
    },
    stickyMineBeep: (urgency = 0) => {
      if (!throttle("smb", 0.07) || !chAudible(CH.sfx)) return;
      const u = Math.max(0, Math.min(1, urgency));
      const f = 520 + u * 680;
      tone(f, 0.055, { type: "square", gain: 0.038 + u * 0.014 });
    },
    castFor: (id) => {
      if (!throttle("c" + id, 0.05) || !chAudible(CH.sfx)) return;
      const cast = spellSfx(id).cast;
      if (cast) { playFile(cast); return; }
      switch (id) {
        case "scourge":   tone(150, 0.5, { type: "sawtooth", gain: 0.05, slideTo: 55 }); break;
        case "sanctuary": tone(440, 0.35, { type: "sine", gain: 0.045, slideTo: 660 }); break;
        case "chainmend": tone(520, 0.2, { type: "sine", gain: 0.04, slideTo: 780 }); break;
        case "frostnova": tone(880, 0.28, { type: "triangle", gain: 0.05, slideTo: 220 }); break;
        case "lightning": noise(0.13, { type: "highpass", freq: 2200, gain: 0.05 }); tone(1300, 0.1, { type: "square", gain: 0.03, slideTo: 380 }); break;
        case "voidbeam":  tone(180, 0.35, { type: "sawtooth", gain: 0.05, slideTo: 90 }); noise(0.28, { type: "lowpass", freq: 400, gain: 0.04, slideTo: 1200 }); break;
        case "voidrip":   tone(120, 0.4, { type: "sawtooth", gain: 0.05, slideTo: 45 }); noise(0.35, { gain: 0.035, freq: 200, slideTo: 80 }); break;
        case "blizzard":  tone(820, 0.22, { type: "triangle", gain: 0.04, slideTo: 420 }); noise(0.2, { type: "highpass", freq: 1400, gain: 0.02 }); break;
        case "teleport": case "thrust": case "windwalk": case "rush": case "timeshift": tone(440, 0.16, { type: "sine", gain: 0.045, slideTo: 900 }); break;
        default:          tone(520, 0.13, { type: "triangle", gain: 0.045, slideTo: 740 });
      }
    },
    impactFor: (id) => {
      if (!chAudible(CH.sfx)) return;
      const throttleKey = id === "firespray" ? "ifs" + (firesprayImpactSeq++ & 15) : "i" + id;
      const throttleGap = id === "firespray" ? 0.02 : 0.04;
      if (!throttle(throttleKey, throttleGap)) return;
      const pool = SPELL_IMPACT_POOLS[id];
      if (pool && pool.length) {
        playFile(pool[Math.floor(Math.random() * pool.length)]);
        return;
      }
      const path = spellSfx(id).impact;
      if (path) { playFile(path); return; }
      Sound.hit();
    },
    endFor: (id) => {
      if (!throttle("e" + id, 0.05) || !chAudible(CH.sfx)) return;
      const path = spellSfx(id).end;
      if (path) playFile(path);
    },
    loopStart: (id) => { /* firewall loops are synced from active walls in Game.syncFirewallLoopAudio */ },
    loopStop: (id) => { if (id === "firewall") haltAllFirewallLoops(true); },
    startFirewallLoop,
    stopFirewallLoop: (key) => haltFirewallLoop(key, true),
    stopAllFirewallLoops: () => haltAllFirewallLoops(true),
    pushPreviewAudio,
    popPreviewAudio,
    cast: (f = 440) => tone(f, 0.12, { type: "triangle", gain: 0.045 }),

    hit: () => { if (!throttle("hit", 0.03)) return; tone(200, 0.12, { type: "square", gain: 0.04, slideTo: 90 }); noise(0.1, { gain: 0.035, freq: 1200, slideTo: 300 }); },
    boom: () => { if (!throttle("boom", 0.04)) return; noise(0.4, { gain: 0.09, freq: 700, slideTo: 110 }); tone(110, 0.4, { type: "sawtooth", gain: 0.06, slideTo: 38 }); },
    fall: () => { tone(520, 0.7, { type: "sine", gain: 0.07, slideTo: 55 }); noise(0.6, { gain: 0.03, freq: 600, slideTo: 120 }); },
    block: () => tone(720, 0.14, { type: "sine", gain: 0.04, slideTo: 520 }),
    blink: () => tone(680, 0.14, { type: "triangle", gain: 0.04, slideTo: 1300 }),
    alarm: () => { if (!throttle("alarm", 0.4) || !chAudible(CH.sfx)) return; tone(880, 0.1, { type: "square", gain: 0.045 }); tone(880, 0.1, { type: "square", gain: 0.045, delay: 0.15 }); },
    updateVoidIndicator,
    stopVoidIndicator: () => { haltVoidIndicator(); setVoidMuffle(false); },
    updateBulletTime,
    bulletTimeStart: (self = false) => {
      beginWorldPitchRamp(BULLET_TIME_RATE, btSfxDuration(BULLET_TIME_START, BULLET_TIME_START_DUR));
      if (!chAudible(CH.sfx)) return;
      playFile(BULLET_TIME_START, { self });
    },
    bulletTimeEndDuration: () => btSfxDuration(BULLET_TIME_END, BULLET_TIME_END_DUR),
    bulletTimeEnd: (self = false) => {
      if (!throttle("bulletTimeEnd", 0.08)) return;
      beginWorldPitchRamp(1, btSfxDuration(BULLET_TIME_END, BULLET_TIME_END_DUR));
      if (!chAudible(CH.sfx)) return;
      playFile(BULLET_TIME_END, { self });
    },
    nukeBeep: (secsLeft = 1) => {
      if (!throttle("nukeBeep", 0.2) || !chAudible(CH.sfx)) return;
      const left = Math.max(1, Math.min(5, secsLeft));
      const urgency = 1 - (left - 1) / 4;
      const f = 240 + urgency * 90;
      tone(f, 0.26, { type: "square", gain: 0.078 + urgency * 0.028, slideTo: f * 0.86 });
      tone(f * 0.5, 0.32, { type: "sine", gain: 0.05 + urgency * 0.018, slideTo: f * 0.42 });
    },
    nukeDetonation: () => {
      if (!throttle("nukeDetonation", 0.4)) return;
      if (!chAudible(CH.sfx)) return;
      playFile(NUKE_DETONATION);
    },
    nukeDetonationDuration: () => btSfxDuration(NUKE_DETONATION, NUKE_DETONATION_DUR),
    playRoundAward: () => {
      if (!throttle("matchAward", 2) || !chAudible(CH.sfx)) return;
      playFile(ROUND_AWARD);
    },
    playReadyCheck: () => {
      if (!throttle("readyCheckGong", 1.2) || !chAudible(CH.sfx)) return;
      playFile(READY_CHECK_GONG);
    },
    playHelloThereEasterEgg: () => {
      if (!throttle("helloThereEasterEgg", 2) || !chAudible(CH.sfx)) return;
      playFile(HELLO_THERE_EASTER_EGG);
    },
    uiHover: () => {
      if (!throttle("uih", 0.06) || !chAudible(CH.ui)) return;
      noise(0.014, { type: "highpass", freq: 1700, q: 0.9, gain: 0.034, channel: CH.ui });
      noise(0.024, { type: "lowpass", freq: 1200, q: 0.85, gain: 0.024, slideTo: 680, channel: CH.ui });
      tone(440, 0.034, { type: "triangle", gain: 0.04, slideTo: 300, attack: 0.002, channel: CH.ui });
      tone(1020, 0.016, { type: "triangle", gain: 0.024, attack: 0.001, delay: 0.003, channel: CH.ui });
    },
    uiClick: () => {
      if (!throttle("uic", 0.03) || !chAudible(CH.ui)) return;
      noise(0.018, { type: "highpass", freq: 2100, q: 1, gain: 0.048, channel: CH.ui });
      noise(0.05, { type: "lowpass", freq: 1500, q: 0.9, gain: 0.044, slideTo: 540, channel: CH.ui });
      tone(410, 0.054, { type: "triangle", gain: 0.07, slideTo: 168, attack: 0.0015, channel: CH.ui });
      tone(860, 0.024, { type: "triangle", gain: 0.036, slideTo: 580, attack: 0.001, delay: 0.004, channel: CH.ui });
      tone(1240, 0.013, { type: "sine", gain: 0.02, attack: 0.001, delay: 0.002, channel: CH.ui });
    },
    ui: () => api.uiClick(),
    countdown: (n) => {
      if (n >= 3) { playFile(COMBAT_START_HORN); return; }
      tone(n <= 0 ? 880 : 520, 0.13, { type: "square", gain: 0.05, channel: CH.sfx });
    },
    draftPickTick: (n) => {
      if (!chAudible(CH.ui)) return;
      if (n <= 3) {
        if (!throttle("dpt", 0.06)) return;
        tone(720 + (3 - n) * 140, 0.042, { type: "square", gain: 0.044, channel: CH.ui });
        tone(980, 0.022, { type: "triangle", gain: 0.03, delay: 0.028, channel: CH.ui });
      } else {
        if (!throttle("dpt", 0.18)) return;
        tone(440, 0.075, { type: "triangle", gain: 0.034, channel: CH.ui });
      }
    },
    win: () => {
      tone(523, 0.15, { type: "triangle", gain: 0.06, channel: CH.music });
      tone(659, 0.15, { type: "triangle", gain: 0.06, delay: 0.13, channel: CH.music });
      tone(784, 0.3, { type: "triangle", gain: 0.06, delay: 0.26, channel: CH.music });
    },
    lose: () => { tone(330, 0.35, { type: "sine", gain: 0.05, slideTo: 150, channel: CH.music }); },
  };
  return api;
})();

// Low-level synth primitives + channel map, surfaced so callers outside this
// module (e.g. engine playFX) can drive one-off FX sounds the same way Sound does.
export const { CH, tone, noise } = Sound;

// Audio credits list (rendered into #audioCreditsList). `path` = in-game asset; Play uses FILE_GAIN.
export const AUDIO_CREDITS = [
  { file: "Combat music — Goliath", path: "audio/combat/goliath_combat_music.mp3", loop: true,
    artist: "<b>Scott Buckley</b>",
    lic: 'Licensed under <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener noreferrer">Creative Commons BY 3.0</a>',
    src: "https://www.youtube.com/watch?v=zOrXW8kA-EY" },
  { file: "Combat music — Dragon Castle", path: "audio/boss/boss_battle_1.mp3", loop: true,
    artist: "<b>Makai Symphony</b>",
    lic: 'Licensed under <a href="https://creativecommons.org/licenses/by-nc/3.0/" target="_blank" rel="noopener noreferrer">Creative Commons BY-NC 3.0</a>',
    src: "https://www.youtube.com/watch?v=9gBTKiVqprE" },
  { file: "Combat music — Endless Storm", path: "audio/boss/boss_battle_2.mp3", loop: true,
    artist: "<b>Makai Symphony</b>",
    lic: 'Licensed under <a href="https://creativecommons.org/licenses/by-nc/3.0/" target="_blank" rel="noopener noreferrer">Creative Commons BY-NC 3.0</a>',
    src: "https://www.youtube.com/watch?v=TNs5JFYaTMk" },
  { file: "Combat music — The Army of Minotaur", path: "audio/boss/boss_battle_3.mp3", loop: true,
    artist: "<b>Makai Symphony</b>",
    lic: 'Licensed under <a href="https://creativecommons.org/licenses/by-nc/3.0/" target="_blank" rel="noopener noreferrer">Creative Commons BY-NC 3.0</a>',
    src: "https://www.youtube.com/watch?v=J0YZKSwe2qU" },
  { file: "Menu UI — Illusory Realm", path: "audio/menu/illusory_realm_ui_music.mp3", loop: true,
    artist: "<b>Darren Curtis</b>", src: "https://www.youtube.com/watch?v=DSemJEC9b6A" },
  { file: "Matchmaking queue — War drums", path: "audio/sfx/ui/war_drums_loop.mp3", loop: true,
    artist: "<b>SoundEffects</b>", src: "https://www.youtube.com/watch?v=dqdCkwlZCDo" },
  { file: "Matchmaking ready check — Gong", path: "audio/sfx/ui/ready_check_gong.mp3", loop: false,
    artist: "<b>Smoking Monkey Media</b> (Free Sound FX)", src: "https://www.youtube.com/watch?v=mVDU3EXlUGk" },
  { file: "Matchmaking queue (reserved) — Dark Mordred's Theme", path: "audio/menu/queue/dark_mordreds_theme_ui_music.mp3", loop: true,
    artist: "<b>Darren Curtis</b>", src: "https://www.youtube.com/watch?v=ZAGC3AhkRME" },
  { file: "Scoreboard & shop — Myst on the Moor", path: "audio/menu/myst_on_the_moor_ui_music.mp3", loop: true,
    artist: "<b>Kevin MacLeod</b> (incompetech.com)",
    lic: 'Licensed under <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener noreferrer">Creative Commons BY 3.0</a>',
    src: "https://www.youtube.com/watch?v=xtm7aRP_lj0" },
  { file: "Scoreboard & shop — Infinitum", path: "audio/menu/infinitum_ui_music.mp3", loop: true,
    artist: "<b>Broken Elegance</b>", src: "https://www.youtube.com/watch?v=NR-do-T_0vM" },
  { file: "Scoreboard & shop — Tempting Secrets", path: "audio/menu/tempting_secrets_ui_music.mp3", loop: true,
    artist: "<b>Kevin MacLeod</b> (incompetech.com)",
    lic: 'Licensed under <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener noreferrer">Creative Commons BY 3.0</a>',
    src: "https://www.youtube.com/watch?v=eSMUf9ozmDU" },
  { file: "Combat horn (light, unused in-game)", path: "audio/combat/start_combat_horn_light.mp3", loop: false,
    artist: '<b>Sound Dimension</b> — <a href="https://www.youtube.com/@sounddimension5" target="_blank" rel="noopener noreferrer">@sounddimension5</a>',
    src: "https://www.youtube.com/watch?v=enQyoFCrSXY" },
  { file: "Combat horn (dark, countdown)", path: "audio/combat/start_combat_horn_dark.mp3", loop: false,
    artist: '<b>Sound Dimension</b> — <a href="https://www.youtube.com/@sounddimension5" target="_blank" rel="noopener noreferrer">@sounddimension5</a>',
    src: "https://www.youtube.com/watch?v=uHHMV8hukBI" },
  { file: "Match victory — Award", path: "audio/sfx/round_award.mp3", loop: false,
    artist: "<b>Miashi Wang</b>", src: "https://www.youtube.com/watch?v=dF1BQmitAL8" },
  { file: "Void warning — Long Riser", path: "audio/sfx/void_indicator.mp3", loop: false,
    artist: "<b>All Sound Effects Music &amp; Films</b>", src: "https://www.youtube.com/watch?v=xKFgTP_JCDs" },
];
